import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {useSelector} from 'react-redux';

import {RootState} from 'appredux/reducers';
import {apiGetFarmerSummary} from 'constants/Api';
import {IFdFarmerSummary} from 'constants/interfaces';

import './FarmersOverviewWidget.scss';

export default function FarmersOverviewWidget(): React.ReactElement {
  const user = useSelector((state: RootState) => state.user);

  const [summary, setSummary] = useState<IFdFarmerSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const crmUserId = parseInt(user.id, 10);

    apiGetFarmerSummary(crmUserId)
      .then((res) => {
        if (alive) setSummary(res);
      })
      .catch((err) => {
        console.error('Failed to load farmer summary for widget', err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (loading) {
    return <Skeleton variant="rect" height={280} style={{borderRadius: 8}} />;
  }

  if (!summary) {
    return (
      <Box className="farmers-overview-widget">
        <Typography className="widget-heading">FARMERS OVERVIEW</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load farmer data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="farmers-overview-widget">
      <Typography className="widget-heading">FARMERS OVERVIEW</Typography>

      <Box className="metrics-row">
        <Box className="metric-card">
          <Typography className="metric-value">
            {summary.totalFarmerCount.toLocaleString()}
          </Typography>
          <Typography className="metric-label">Total Farmers</Typography>
        </Box>
        <Box className="metric-card">
          <Typography className="metric-value">
            {summary.avgFarmSizeHectares.toFixed(1)} ha
          </Typography>
          <Typography className="metric-label">Avg Farm Size</Typography>
        </Box>
      </Box>

      <Box className="breakdowns">
        {summary.provinceBreakdown?.length > 0 && (
          <Box className="breakdown-section">
            <Typography className="section-title">By Province</Typography>
            {summary.provinceBreakdown.map((pb, idx) => (
              <Box key={idx} className="breakdown-row">
                <Typography className="name">{pb.province}</Typography>
                <Typography className="count">{pb.farmerCount}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {summary.cropBreakdown?.length > 0 && (
          <Box className="breakdown-section">
            <Typography className="section-title">By Crop</Typography>
            {summary.cropBreakdown.map((cb, idx) => (
              <Box key={idx} className="breakdown-row">
                <Typography className="name">{cb.crop}</Typography>
                <Typography className="count">{cb.farmerCount}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
